import { MultilingualLanguages } from '@asgard/api-common';
import JobOfferComponent from 'components/jobOffer/JobOfferComponent';
import JobOfferPageHead from 'components/jobOffer/JobOfferPageHead';
import WIPFeaturesContext from 'context/wip-features/wip-features.context';
import { usePageTrack } from 'hooks/usePageTrack';
import { WebOffer } from 'model/model.web-offer';
import { GetStaticProps } from 'next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useRouter } from 'next/dist/client/router';
import { useEffect, useState } from 'react';
import { DEFAULT_LOCALE } from 'services/multilingual.service';
import { ServiceWebOffer } from 'services/service.web-offer';
import { getOfferUrl } from 'services/ui/offer.service';
import { createGlobalStyle } from 'styled-components';
import { APP_URLS, getAppUrl, getNotFoundUrl } from 'utils/app-urls';
import { config } from 'utils/config';
import { avoidRateLimit } from 'utils/rate-limit';

type OfferPageProps = {
  offer: WebOffer;
  slug?: string;
};

const OfferPage: React.FC<OfferPageProps> = ({ offer, slug }) => {
  const router = useRouter();
  const [origin, setOrigin] = useState('');

  usePageTrack('Website - Page Loaded');

  useEffect(() => {
    setOrigin(window.location.origin);
  }, []);

  useEffect(() => {
    const offerSlug = offer.slug;

    if (slug && shouldRedirectToSlug(slug, offerSlug)) {
      router.push(
        `${getAppUrl(APP_URLS.jobOffer, router.locale)}${getOfferUrl(
          offer,
          router.locale
        )}`
      );
    }
  }, []);

  const JobOfferGlobalStyles = createGlobalStyle`
    #freshworks-container iframe {
      bottom: 90px !important;
    }
  `;

  return (
    <>
      <JobOfferGlobalStyles />
      <JobOfferPageHead
        url={APP_URLS.jobOffer}
        offer={offer}
        locale={router.locale}
        origin={origin}
      />
      <WIPFeaturesContext.Provider value={false}>
        <JobOfferComponent offer={offer} origin={origin} />
      </WIPFeaturesContext.Provider>
    </>
  );
};

export default OfferPage;

export async function getStaticPaths() {
  await avoidRateLimit();
  const offersES = (
    await ServiceWebOffer.getAllSummary(
      { onlyActive: true },
      'ES',
      'public-endpoint'
    )
  ).slice(0, +config.pregenerateOffers);

  const offersEN = (
    await ServiceWebOffer.getAllSummary(
      { onlyActive: true },
      'EN',
      'public-endpoint'
    )
  ).slice(0, +config.pregenerateOffers);

  const pathsES = offersES
    .map((offer) => ({
      params: {
        slug: offer.slug,
        id: offer.id.toString()
      },
      locale: 'es'
    }))
    .filter((p) => !!p.params.slug);

  const pathsEN = offersEN
    .map((offer) => ({
      params: {
        slug: offer.slug,
        id: offer.id.toString()
      },
      locale: 'en'
    }))
    .filter((p) => !!p.params.slug);

  return {
    paths: [...pathsES, ...pathsEN],
    fallback: 'blocking'
  };
}

function shouldRedirectToSlug(slugParam: string, offerSlug: string): boolean {
  return !slugParam || slugParam !== offerSlug;
}

export const getStaticProps: GetStaticProps<OfferPageProps> = async ({
  params,
  locale
}) => {
  try {
    if (!params?.id) {
      throw new Error('No id param found');
    }

    const offerId = +params.id.toString();

    if (isNaN(offerId)) {
      return {
        redirect: {
          destination: getNotFoundUrl(locale),
          permanent: false
        }
      };
    }

    const offer = await ServiceWebOffer.getById(
      offerId,
      locale?.toUpperCase() as MultilingualLanguages,
      'public-endpoint'
    );

    if (!offer) {
      throw new Error('No offer found');
    }

    return {
      props: {
        offer: JSON.parse(JSON.stringify(offer)),
        slug: params.slug?.toString(),
        ...(await serverSideTranslations(locale || DEFAULT_LOCALE, [
          'common',
          'job-offer',
          'apply-modal',
          'topbar',
          'footer'
        ]))
      },
      revalidate: 30
    };
  } catch (ex) {
    return {
      revalidate: 1, // this is an error, it should be revalidated as soon as possible
      redirect: {
        permanent: false,
        destination: `${getAppUrl(
          APP_URLS.jobOffers,
          locale
        )}?offer-cancelled=true`
      }
    };
  }
};
